/**
 * /prompts/:id/assistant/history — Prompt Assistant session history (Story
 * 8.5 AC5).
 *
 * Lists past assistant sessions for a prompt with pagination + status
 * filter. Clicking a row opens the detail panel (session metadata + full
 * message thread) inline so the operator can audit decisions without losing
 * the list context.
 *
 * RBAC (AC7): viewer+ — viewers can read but the "Iniciar nova sessão" CTA
 * is hidden for them; clicking the CTA on operator/admin navigates back to
 * `/prompts/:id/assistant` to start a fresh chat.
 */

import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api';
import { formatDateTime as fmtDateTime } from '@/lib/format-date';
import { ChatMessage } from '@/components/assistant/ChatMessage';
import { ProposalCard } from '@/components/assistant/ProposalCard';
import { useAuth } from '@/hooks/useAuth';
import {
  getSessionDetail,
  listSessions,
  type AssistantSession,
  type AssistantSessionStatus,
  type ListSessionsResponse,
  type SessionDetailResponse,
} from '@/lib/promptAssistant';

const PAGE_SIZE = 20;

const STATUS_FILTERS: Array<{ value: AssistantSessionStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'open', label: 'Abertos' },
  { value: 'applied', label: 'Aplicados' },
  { value: 'discarded', label: 'Descartados' },
];

function formatDateTime(iso: string): string {
  return fmtDateTime(iso) || iso;
}

function statusClass(status: AssistantSessionStatus): string {
  switch (status) {
    case 'applied':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    case 'discarded':
      return 'bg-muted text-muted-foreground';
    case 'open':
    default:
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-400';
  }
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { message?: string } };
    return body?.error?.message ?? `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function PromptAssistantHistoryPage() {
  const { id: promptId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const role = user?.role ?? 'viewer';
  const canStartSession = role === 'operator' || role === 'admin';

  const [statusFilter, setStatusFilter] = useState<AssistantSessionStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const [openSessionId, setOpenSessionId] = useState<string | null>(null);

  const listQuery = useQuery<ListSessionsResponse>({
    queryKey: ['admin', 'assistant-sessions', promptId, statusFilter, page],
    queryFn: () =>
      listSessions({
        promptId: promptId!,
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
    enabled: Boolean(promptId),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const detailQuery = useQuery<SessionDetailResponse>({
    queryKey: ['admin', 'assistant-session-detail', openSessionId],
    queryFn: () => getSessionDetail(openSessionId!),
    enabled: Boolean(openSessionId),
    staleTime: 30_000,
  });

  const totalPages = useMemo(() => {
    const total = listQuery.data?.total ?? 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [listQuery.data?.total]);

  if (!promptId) {
    return <Navigate to="/prompts" replace />;
  }

  const items: AssistantSession[] = listQuery.data?.data ?? [];

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/prompts/${promptId}`} aria-label="Voltar para detalhe do prompt">
              <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
            </Link>
          </Button>
          <h1 className="text-lg font-semibold">Histórico do assistente</h1>
        </div>
        {canStartSession ? (
          <Button asChild>
            <Link to={`/prompts/${promptId}/assistant`}>
              <Plus className="mr-1 h-4 w-4" /> Iniciar nova sessão
            </Link>
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((opt) => (
          <Button
            key={opt.value}
            variant={statusFilter === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setStatusFilter(opt.value);
              setPage(1);
            }}
            data-testid={`history-filter-${opt.value}`}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm">
            {items.length === 0 && !listQuery.isLoading
              ? 'Nenhuma sessão encontrada'
              : `${listQuery.data?.total ?? 0} sessões`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {listQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Carregando…</div>
          ) : null}
          {listQuery.isError ? (
            <div className="rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              {humanizeError(listQuery.error, 'Erro ao carregar histórico')}
            </div>
          ) : null}
          {items.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() =>
                setOpenSessionId((current) => (current === session.id ? null : session.id))
              }
              data-testid={`history-row-${session.id}`}
              className={`w-full rounded border p-3 text-left text-sm transition-colors ${
                openSessionId === session.id ? 'border-primary bg-primary/5' : 'hover:bg-accent'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium">
                  {session.userDescription || '(sem descrição)'}
                </span>
                <span className={`rounded px-2 py-0.5 text-xs ${statusClass(session.status)}`}>
                  {session.status}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>
                  <MessageSquare className="mr-1 inline h-3 w-3" />
                  {session.referenceConversationCount} ref
                </span>
                <span>{formatDateTime(session.createdAt)}</span>
                {session.appliedVersionId ? (
                  <span>
                    →{' '}
                    <Link
                      to={`/prompts/${promptId}/versions/${session.appliedVersionId}`}
                      className="underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ver versão
                    </Link>
                  </span>
                ) : null}
              </div>

              {openSessionId === session.id ? (
                <div className="mt-3 space-y-3 border-t pt-3">
                  {detailQuery.isLoading ? (
                    <div className="text-xs text-muted-foreground">Carregando transcript…</div>
                  ) : null}
                  {detailQuery.isError ? (
                    <div className="rounded border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
                      {humanizeError(detailQuery.error, 'Erro ao carregar sessão')}
                    </div>
                  ) : null}
                  {detailQuery.data?.messages.map((m) => (
                    <div key={m.id} className="space-y-2">
                      <ChatMessage
                        role={m.role}
                        content={m.content}
                        createdAt={m.createdAt}
                        tokensTotal={m.openaiTokensTotal}
                        latencyMs={m.openaiLatencyMs}
                      />
                      {m.role === 'assistant' && (m.proposedChanges?.length ?? 0) > 0 ? (
                        <ProposalCard
                          analysis=""
                          proposedChanges={m.proposedChanges ?? []}
                          proposedCodeChanges={m.proposedCodeChanges ?? []}
                          role="viewer"
                          alreadyResolved={true}
                          onApply={() => undefined}
                          onDiscard={() => undefined}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </button>
          ))}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          ← Anterior
        </Button>
        <span>
          Página {page} de {totalPages}
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          Próxima →
        </Button>
      </div>
    </div>
  );
}
